import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { MdChevronLeft, MdChevronRight, MdToday, MdCallSplit, MdRemoveCircleOutline, MdDelete, MdWarning } from "react-icons/md";
import { toast } from "react-toastify";
import {
  createTask,
  getBoardColumns,
  getSchedules,
  getTasks,
  updateTask,
  updateTaskSchedule,
  updateTaskStatus,
} from "../../../../services/projectTaskApi";
import { getTimeline } from "../../../../services/capacityApi";
import { BoardColumn, ProjectTask, TaskStatus, TaskType } from "../../../../interfaces/ProjectTask";
import { epicColor, EpicColor } from "../../../../utils/epicColor";
import SearchableSelect from "../../../../components/searchableSelect/SearchableSelect";
import TaskModal from "../../projectTasks/components/TaskModal";

interface Line {
  date: string; // yyyy-mm-dd
  plannedHours: number;
}
interface Option {
  id: number;
  name: string;
}
interface Props {
  employeeId: number;
  employeeName: string;
  employees: Option[];
  start: Date;
  weeks: number;
  /**
   * Permite "quebrar" a tarefa para outro responsável (cria tarefa no nome de terceiro e reduz a
   * estimativa da original) — ato de gestão. O colaborador planejando a própria semana recebe
   * `false`: ele move/insere/remove horas das tarefas dele, mas não redistribui trabalho.
   */
  canSplit?: boolean;
}

const DAY_MS = 86400000;
const pad = (n: number) => String(n).padStart(2, "0");
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (k: string) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);
const isoMonday = (d: Date) => {
  const x = startOfDay(d);
  return new Date(x.getTime() - (((x.getDay() + 6) % 7) * DAY_MS));
};
const weekKeyOf = (iso: string) => toIso(isoMonday(parseIso(iso)));
const fmtWeek = (iso: string) => parseIso(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
const round2 = (n: number) => Math.round(n * 100) / 100;

type Pending =
  | { kind: "insert"; task: ProjectTask; week: string; defaultHours: number }
  | { kind: "partial"; task: ProjectTask; week: string; defaultHours: number }
  | { kind: "split"; task: ProjectTask; week: string; defaultHours: number };

const CapacityEmployeePlanner: React.FC<Props> = ({ employeeId, employeeName, employees, start, weeks, canSplit = true }) => {
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [draft, setDraft] = useState<Record<number, Line[]>>({});
  const [capByWeek, setCapByWeek] = useState<Map<string, number>>(new Map());
  const [weekStart, setWeekStart] = useState<Date>(isoMonday(start));
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  // Clique no card abre o mesmo modal de detalhes/edição da tela de Tarefas.
  const [editing, setEditing] = useState<ProjectTask | null>(null);
  const [columns, setColumns] = useState<BoardColumn[]>([]);

  // O dnd chama preventDefault() no click residual de um drag — sem este guard, todo
  // arraste abriria o modal. As colunas do board (seletor de status do modal) são
  // buscadas sob demanda no 1º clique, não no mount (há um planner por colaborador).
  const openTask = (e: React.MouseEvent, task: ProjectTask) => {
    if (e.defaultPrevented) return;
    if (columns.length === 0)
      getBoardColumns()
        .then(setColumns)
        .catch((err) => {
          console.error("Erro ao carregar colunas do board", err);
          toast.error("Erro ao carregar as etapas do board.");
        });
    setEditing(task);
  };

  const weekList = useMemo(
    () => Array.from({ length: weeks }, (_, i) => toIso(addDays(weekStart, i * 7))),
    [weekStart, weeks]
  );

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      getTasks(),
      getSchedules(),
      getTimeline(weekStart, weeks).catch(() => null),
    ])
      .then(([tk, sch, tl]) => {
        setTasks(tk);
        const map: Record<number, Line[]> = {};
        sch.forEach((r) => {
          map[r.taskId] = (r.lines ?? []).map((l) => ({ date: String(l.date).slice(0, 10), plannedHours: l.plannedHours }));
        });
        setDraft(map);
        const cap = new Map<string, number>();
        const row = tl?.rows?.find((r) => r.employeeId === employeeId);
        row?.cells?.forEach((c) => cap.set(weekKeyOf(String(c.weekStart).slice(0, 10)), c.capacityHours));
        setCapByWeek(cap);
      })
      .catch((e) => console.error("Erro ao carregar planejador", e))
      .finally(() => setLoading(false));
  }, [employeeId, weekStart, weeks]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => setWeekStart(isoMonday(start)), [start]);

  // ── Derivados ───────────────────────────────────────────────────────────────
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const parentIds = useMemo(() => {
    const s = new Set<number>();
    tasks.forEach((t) => t.parentTaskId != null && s.add(t.parentTaskId));
    return s;
  }, [tasks]);
  const isLeaf = (t: ProjectTask) => !parentIds.has(t.id);

  // ── Agrupamento por hierarquia (como no Kanban de Tarefas): cada card se agrupa pelo
  //    PAI direto — tarefa sob o épico, subtarefa sob a tarefa. Cor herdada do épico. ──
  const epicOf = (t: ProjectTask): ProjectTask | null => {
    let cur: ProjectTask | undefined = t;
    let guard = 0;
    while (cur && guard++ < 12) {
      if (cur.type === TaskType.Epic) return cur;
      if (cur.parentTaskId == null) return null;
      cur = taskById.get(cur.parentTaskId);
    }
    return null;
  };

  // Breadcrumb do grupo: "Épico › Tarefa" (ancestrais + o próprio pai).
  const pathOf = (t: ProjectTask): string => {
    const names = [t.title];
    let cur = t.parentTaskId != null ? taskById.get(t.parentTaskId) : undefined;
    let guard = 0;
    while (cur && guard++ < 10) {
      names.unshift(cur.title);
      cur = cur.parentTaskId != null ? taskById.get(cur.parentTaskId) : undefined;
    }
    return names.join(" › ");
  };

  type PlannerEntry =
    | { kind: "header"; id: number; title: string; count: number; accent: EpicColor }
    | { kind: "card"; task: ProjectTask; index: number; accent: EpicColor | null };

  // Monta as entradas de uma coluna agrupadas pelo pai direto, com índices de Draggable
  // CONTÍGUOS (0..n-1) — os cabeçalhos não são Draggable e ficam fora da sequência.
  const groupByHierarchy = (items: ProjectTask[]): PlannerEntry[] => {
    const groups = new Map<number | "none", { parent: ProjectTask | null; items: ProjectTask[] }>();
    items.forEach((t) => {
      const parent = t.parentTaskId != null ? taskById.get(t.parentTaskId) ?? null : null;
      const key = parent?.id ?? "none";
      const g = groups.get(key) ?? { parent, items: [] };
      g.items.push(t);
      groups.set(key, g);
    });

    // Ordena: pelo épico ancestral, depois pelo pai; "sem grupo" por último.
    const epicOrder = (p: ProjectTask) => epicOf(p)?.orderIndex ?? Number.MAX_SAFE_INTEGER;
    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (!a.parent) return 1;
      if (!b.parent) return -1;
      return (
        epicOrder(a.parent) - epicOrder(b.parent) ||
        a.parent.orderIndex - b.parent.orderIndex ||
        a.parent.id - b.parent.id
      );
    });

    const entries: PlannerEntry[] = [];
    let di = 0;
    ordered.forEach((g) => {
      const accent = g.parent ? epicColor(epicOf(g.parent)?.id ?? g.parent.id) : null;
      if (g.parent && accent)
        entries.push({ kind: "header", id: g.parent.id, title: pathOf(g.parent), count: g.items.length, accent });
      g.items.forEach((task) => entries.push({ kind: "card", task, index: di++, accent }));
    });
    return entries;
  };

  // Estilo do card dentro de um grupo: borda na cor do épico + leve recuo sob o cabeçalho.
  const accentStyle = (accent: EpicColor | null): React.CSSProperties =>
    accent ? { borderLeft: `3px solid ${accent.solid}`, marginLeft: 6 } : {};

  const scheduledTotal = (taskId: number) => (draft[taskId] ?? []).reduce((a, l) => a + l.plannedHours, 0);
  const weekHours = (taskId: number, week: string) =>
    (draft[taskId] ?? []).filter((l) => weekKeyOf(l.date) === week).reduce((a, l) => a + l.plannedHours, 0);

  const eligible = (t: ProjectTask) =>
    (t.estimatedHours ?? 0) > 0 &&
    t.statusCategory !== "Done" &&
    t.statusCategory !== "Cancelled" &&
    t.statusCategory !== "OnHold" &&
    isLeaf(t);

  // Backlog: tarefas do colaborador OU sem responsável, elegíveis, com horas restantes.
  const backlog = useMemo(
    () =>
      tasks.filter(
        (t) =>
          eligible(t) &&
          (t.assigneeEmployeeId === employeeId || t.assigneeEmployeeId == null) &&
          round2((t.estimatedHours ?? 0) - scheduledTotal(t.id)) > 0.001
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, draft, employeeId, parentIds]
  );

  // Tarefas do colaborador com horas numa semana — só FOLHAS (pais não consomem; evita duplicar horas).
  const tasksInWeek = (week: string) =>
    tasks.filter((t) => t.assigneeEmployeeId === employeeId && isLeaf(t) && weekHours(t.id, week) > 0.001);

  const usedInWeek = (week: string) =>
    tasks
      .filter((t) => t.assigneeEmployeeId === employeeId && isLeaf(t))
      .reduce((a, t) => a + weekHours(t.id, week), 0);

  // Atualiza uma tarefa no estado local (otimista, sem refetch).
  const patchTask = (id: number, partial: Partial<ProjectTask>) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...partial } : t)));

  // ── Edição do cronograma: OTIMISTA. Atualiza o estado local na hora e persiste em 2º plano,
  //    SEM recarregar a tela (capacity não muda ao mover horas). Faz rollback se a API falhar. ──
  const persist = (taskId: number, lines: Line[]) => {
    const clean = lines.filter((l) => l.plannedHours > 0.001).map((l) => ({ ...l, plannedHours: round2(l.plannedHours) }));
    const prevLines = draft[taskId] ?? [];
    setDraft((p) => ({ ...p, [taskId]: clean })); // otimista
    updateTaskSchedule(taskId, clean).catch((e) => {
      console.error("Erro ao salvar cronograma", e);
      toast.error("Erro ao salvar o cronograma.");
      setDraft((p) => ({ ...p, [taskId]: prevLines })); // rollback
    });
  };

  // Linhas de uma tarefa numa semana (distribui seg–sex), mantendo as outras semanas.
  const buildWeekLines = (taskId: number, week: string, hours: number): Line[] => {
    const kept = (draft[taskId] ?? []).filter((l) => weekKeyOf(l.date) !== week);
    if (hours <= 0.001) return kept;
    const mon = parseIso(week);
    const per = round2(hours / 5);
    const days: Line[] = [];
    for (let i = 0; i < 5; i++) days.push({ date: toIso(addDays(mon, i)), plannedHours: per });
    return [...kept, ...days];
  };

  // Linhas após remover X horas a partir de uma semana (transbordando p/ as seguintes).
  const removeHoursLines = (taskId: number, fromWeek: string, x: number): Line[] => {
    let remaining = x;
    const weeksSorted = Array.from(new Set((draft[taskId] ?? []).map((l) => weekKeyOf(l.date)))).sort();
    const ordered = [fromWeek, ...weeksSorted.filter((w) => w > fromWeek), ...weeksSorted.filter((w) => w < fromWeek)];
    let lines = draft[taskId] ?? [];
    for (const w of ordered) {
      if (remaining <= 0.001) break;
      const cur = weekHours(taskId, w);
      if (cur <= 0) continue;
      const take = Math.min(cur, remaining);
      const newH = round2(cur - take);
      const kept = lines.filter((l) => weekKeyOf(l.date) !== w);
      const mon = parseIso(w);
      const per = round2(newH / 5);
      const days = newH > 0.001 ? Array.from({ length: 5 }, (_, i) => ({ date: toIso(addDays(mon, i)), plannedHours: per })) : [];
      lines = [...kept, ...days];
      remaining -= take;
    }
    return lines;
  };

  const doInsert = async (task: ProjectTask, week: string, hours: number) => {
    // Sem responsável → atribui ao colaborador; em Backlog → vai p/ "A Fazer" (otimista + bg).
    if (task.assigneeEmployeeId == null) {
      patchTask(task.id, { assigneeEmployeeId: employeeId, assignees: [{ id: employeeId }], status: task.statusCategory === "NotStarted" ? TaskStatus.Todo : task.status });
      try {
        // AGUARDA: o servidor só deixa o colaborador planejar tarefa dele, então o cronograma
        // não pode chegar antes da atribuição (daria 403 e o card "voltaria" sozinho).
        await updateTask({
          id: task.id, projectId: task.projectId, parentTaskId: task.parentTaskId ?? null, type: task.type,
          title: task.title, description: task.description, estimatedHours: task.estimatedHours ?? null,
          assigneeEmployeeIds: [employeeId], status: task.statusCategory === "NotStarted" ? TaskStatus.Todo : task.status,
          priority: task.priority, dependsOnTaskIds: task.dependsOnTaskIds ?? [], scheduleLocked: task.scheduleLocked,
        });
      } catch (e) {
        console.error(e);
        toast.error("Erro ao atribuir responsável.");
        patchTask(task.id, { assigneeEmployeeId: null, assignees: [], status: task.status });
        return;
      }
    } else if (task.statusCategory === "NotStarted") {
      patchTask(task.id, { status: TaskStatus.Todo });
      updateTaskStatus(task.id, TaskStatus.Todo).catch((e) => console.error(e));
    }
    persist(task.id, buildWeekLines(task.id, week, weekHours(task.id, week) + hours));
  };

  const doSetWeek = (task: ProjectTask, week: string, hours: number) =>
    persist(task.id, buildWeekLines(task.id, week, hours));

  const doMove = (task: ProjectTask, from: string, to: string) => {
    const hrs = weekHours(task.id, from);
    const lines = (draft[task.id] ?? []).filter((l) => weekKeyOf(l.date) !== from && weekKeyOf(l.date) !== to);
    const mon = parseIso(to);
    const per = round2((weekHours(task.id, to) + hrs) / 5);
    for (let i = 0; i < 5; i++) lines.push({ date: toIso(addDays(mon, i)), plannedHours: per });
    persist(task.id, lines);
  };

  const doSplit = (task: ProjectTask, fromWeek: string, otherEmployeeId: number, hours: number) => {
    const x = Math.min(hours, task.estimatedHours ?? hours);
    // Otimista: reduz a estimativa da original e tira X horas do cronograma dela.
    patchTask(task.id, { estimatedHours: round2((task.estimatedHours ?? 0) - x) });
    persist(task.id, removeHoursLines(task.id, fromWeek, x));
    // Cria a nova tarefa do outro responsável + atualiza a estimativa da original (2º plano).
    createTask({
      projectId: task.projectId, parentTaskId: task.parentTaskId ?? null, type: task.type ?? TaskType.Task,
      title: `${task.title} (parte)`, description: task.description, estimatedHours: x,
      assigneeEmployeeIds: [otherEmployeeId], status: TaskStatus.Backlog, priority: task.priority,
    }).catch((e) => { console.error(e); toast.error("Erro ao criar a tarefa do outro responsável."); });
    updateTask({
      id: task.id, projectId: task.projectId, parentTaskId: task.parentTaskId ?? null, type: task.type,
      title: task.title, description: task.description, estimatedHours: round2((task.estimatedHours ?? 0) - x),
      assigneeEmployeeIds: task.assignees?.length ? task.assignees.map((a) => a.id) : task.assigneeEmployeeId ? [task.assigneeEmployeeId] : [],
      status: task.status, priority: task.priority, dependsOnTaskIds: task.dependsOnTaskIds ?? [], scheduleLocked: task.scheduleLocked,
    }).catch((e) => console.error(e));
    toast.success(`Tarefa quebrada: ${x}h para ${employees.find((e) => e.id === otherEmployeeId)?.name ?? "outro responsável"}.`);
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────────
  const onDragEnd = (r: DropResult) => {
    if (!r.destination) return;
    const dest = r.destination.droppableId;
    const [kind, idStr, wk] = r.draggableId.split(":");
    const task = taskById.get(Number(idStr));
    if (!task) return;
    if (dest === "backlog") {
      if (kind === "w") doSetWeek(task, wk, 0); // remover inteira da semana
      return;
    }
    // destino é uma semana
    if (kind === "b") {
      const remaining = round2((task.estimatedHours ?? 0) - scheduledTotal(task.id));
      setPending({ kind: "insert", task, week: dest, defaultHours: remaining > 0 ? remaining : task.estimatedHours ?? 0 });
    } else if (kind === "w" && wk !== dest) {
      doMove(task, wk, dest);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const empName = employeeName;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-navy-700 dark:text-white">Planejamento — {empName}</h4>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Semanas anteriores" onClick={() => setWeekStart((p) => new Date(p.getTime() - weeks * 7 * DAY_MS))} className="rounded-xl border border-gray-200 p-1.5 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"><MdChevronLeft size={16} aria-hidden="true" /></button>
          <button type="button" onClick={() => setWeekStart(isoMonday(new Date()))} className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"><MdToday size={14} aria-hidden="true" /> Hoje</button>
          <button type="button" aria-label="Próximas semanas" onClick={() => setWeekStart((p) => new Date(p.getTime() + weeks * 7 * DAY_MS))} className="rounded-xl border border-gray-200 p-1.5 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"><MdChevronRight size={16} aria-hidden="true" /></button>
        </div>
      </div>

      {loading && tasks.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {/* Backlog */}
            <Column droppableId="backlog" title="Backlog" subtitle={`${backlog.length} disponível(is)`} accent>
              {groupByHierarchy(backlog).map((entry) => {
                if (entry.kind === "header")
                  return <GroupHeader key={`h:${entry.id}`} title={entry.title} count={entry.count} accent={entry.accent} />;
                const t = entry.task;
                const remaining = round2((t.estimatedHours ?? 0) - scheduledTotal(t.id));
                return (
                  <Draggable key={t.id} draggableId={`b:${t.id}`} index={entry.index}>
                    {(prov, snap) => {
                      const card = (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        style={{ ...prov.draggableProps.style, ...accentStyle(entry.accent) }}
                        onClick={(e) => openTask(e, t)}
                        title="Abrir detalhes da tarefa"
                        className={`cursor-pointer rounded-xl bg-white p-2.5 dark:bg-navy-800 ${snap.isDragging ? "shadow-2xl ring-2 ring-dv-blue-400" : "shadow-sm hover:shadow-md"}`}
                      >
                        <p className="truncate text-xs font-semibold text-navy-700 dark:text-white">{t.title}</p>
                        <div className="mt-1 flex items-center justify-between gap-1 text-[10px] text-gray-700 dark:text-gray-300">
                          <span className="min-w-0 truncate">{t.projectName ?? ""}{t.assigneeEmployeeId == null ? " · sem responsável" : ""}</span>
                          <span className="shrink-0 rounded-md bg-dv-blue-50 px-1.5 py-0.5 font-bold tabular-nums text-dv-blue-600 dark:bg-dv-blue-500/10 dark:text-dv-cyan-300">{remaining}h</span>
                        </div>
                      </div>
                      );
                      return snap.isDragging ? createPortal(card, document.body) : card;
                    }}
                  </Draggable>
                );
              })}
            </Column>

            {/* Semanas */}
            {weekList.map((wk) => {
              const cap = capByWeek.get(wk) ?? 40;
              const used = round2(usedInWeek(wk));
              const free = round2(cap - used);
              const pct = cap > 0 ? used / cap : used > 0 ? 1 : 0;
              const level = pct >= 1 ? "red" : pct >= 0.8 ? "amber" : "green";
              const items = tasksInWeek(wk);
              return (
                <Column
                  droppableId={wk}
                  key={wk}
                  title={`Sem ${fmtWeek(wk)}`}
                  subtitle={`${items.length} tarefa(s)`}
                  chip={{ used, cap, free, level }}
                >
                  {groupByHierarchy(items).map((entry) => {
                    if (entry.kind === "header")
                      return <GroupHeader key={`h:${entry.id}:${wk}`} title={entry.title} count={entry.count} accent={entry.accent} />;
                    const t = entry.task;
                    const h = round2(weekHours(t.id, wk));
                    return (
                      <Draggable key={`${t.id}:${wk}`} draggableId={`w:${t.id}:${wk}`} index={entry.index}>
                        {(prov, snap) => {
                          const card = (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            style={{ ...prov.draggableProps.style, ...accentStyle(entry.accent) }}
                            onClick={(e) => openTask(e, t)}
                            title="Abrir detalhes da tarefa"
                            className={`group cursor-pointer rounded-xl bg-white p-2.5 dark:bg-navy-800 ${snap.isDragging ? "shadow-2xl ring-2 ring-dv-blue-400" : "shadow-sm hover:shadow-md"}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <p className="truncate text-xs font-semibold text-navy-700 dark:text-white">{t.title}</p>
                              <span className="shrink-0 rounded-md bg-dv-blue-50 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-dv-blue-600 dark:bg-dv-blue-500/10 dark:text-dv-cyan-300">{h}h</span>
                            </div>
                            <p className="mt-0.5 truncate text-[10px] text-gray-700 dark:text-gray-300">{t.projectName ?? ""}</p>
                            <div className="mt-1.5 hidden items-center gap-2 text-gray-500 group-hover:flex dark:text-gray-300">
                              <button type="button" aria-label="Remover parte das horas" onClick={(e) => { e.stopPropagation(); setPending({ kind: "partial", task: t, week: wk, defaultHours: h }); }} title="Remover parte das horas" className="transition-colors hover:text-amber-600 dark:hover:text-amber-400"><MdRemoveCircleOutline size={15} aria-hidden="true" /></button>
                              {canSplit && (
                                <button type="button" aria-label="Quebrar para outro responsável" onClick={(e) => { e.stopPropagation(); setPending({ kind: "split", task: t, week: wk, defaultHours: h }); }} title="Quebrar para outro responsável" className="transition-colors hover:text-dv-blue-600 dark:hover:text-dv-cyan-300"><MdCallSplit size={15} aria-hidden="true" /></button>
                              )}
                              <button type="button" aria-label="Remover a tarefa desta semana" onClick={(e) => { e.stopPropagation(); doSetWeek(t, wk, 0); }} title="Remover a tarefa desta semana" className="transition-colors hover:text-red-600 dark:hover:text-red-400"><MdDelete size={15} aria-hidden="true" /></button>
                            </div>
                          </div>
                          );
                          return snap.isDragging ? createPortal(card, document.body) : card;
                        }}
                      </Draggable>
                    );
                  })}
                </Column>
              );
            })}
          </div>
        </DragDropContext>
      )}

      <p className="text-[11px] text-gray-700 dark:text-gray-300">
        Arraste do Backlog para uma semana (você informa as horas), entre semanas para mover, ou para o Backlog
        para remover. Use os botões do cartão para remover parte das horas
        {canSplit ? " ou quebrar a tarefa para outro responsável" : ""}.
      </p>

      {editing && (
        <TaskModal
          open
          projectId={editing.projectId}
          employees={employees}
          tasks={tasks}
          columns={columns}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}

      {pending && (
        <PendingModal
          pending={pending}
          employees={employees.filter((e) => e.id !== employeeId)}
          onClose={() => setPending(null)}
          onInsert={(hours) => { const p = pending; setPending(null); doInsert(p.task, p.week, hours); }}
          onPartial={(hours) => { const p = pending; setPending(null); doSetWeek(p.task, p.week, Math.max(0, round2(p.defaultHours - hours))); }}
          onSplit={(other, hours) => { const p = pending; setPending(null); doSplit(p.task, p.week, other, hours); }}
        />
      )}
    </div>
  );
};

// ── Cabeçalho de grupo (épico/tarefa-pai) ───────────────────────────────────────
const GroupHeader: React.FC<{ title: string; count: number; accent: EpicColor }> = ({ title, count, accent }) => (
  <div className="mt-1 flex items-center gap-1.5 rounded-lg px-2 py-1 first:mt-0" style={{ background: accent.tint }}>
    <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: accent.solid }} />
    <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-wide" style={{ color: accent.text }} title={title}>
      {title}
    </span>
    <span className="shrink-0 rounded-full bg-white/70 px-1.5 text-[10px] font-bold" style={{ color: accent.text }}>
      {count}
    </span>
  </div>
);

// ── Column ──────────────────────────────────────────────────────────────────────
const Column: React.FC<{
  droppableId: string;
  title: string;
  subtitle: string;
  accent?: boolean;
  chip?: { used: number; cap: number; free: number; level: "red" | "amber" | "green" };
  children: React.ReactNode;
}> = ({ droppableId, title, subtitle, accent, chip, children }) => (
  <Droppable droppableId={droppableId}>
    {(prov, snap) => (
      <div
        ref={prov.innerRef}
        {...prov.droppableProps}
        className={`flex w-56 shrink-0 flex-col rounded-2xl p-2.5 transition-colors duration-200 ${
          snap.isDraggingOver ? "bg-dv-blue-50 ring-2 ring-dv-blue-300 dark:bg-navy-900/70" : accent ? "bg-gray-100 dark:bg-navy-900" : "bg-gray-50 dark:bg-navy-900/60"
        }`}
      >
        <div className="px-1">
          <span className="text-xs font-bold text-navy-700 dark:text-white">{title}</span>
          <span className="ml-1 text-[10px] text-gray-700 dark:text-gray-300">{subtitle}</span>
        </div>
        {chip && (
          <div
            className={`mt-1 inline-flex items-center gap-1 self-start rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              chip.level === "red" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : chip.level === "amber" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
            }`}
            title={`${chip.used}h de ${chip.cap}h · ${chip.free}h livres`}
          >
            {chip.level === "red" && <MdWarning size={10} aria-hidden="true" />} {chip.used}/{chip.cap}h · livre {chip.free}h
          </div>
        )}
        <div className="mt-2 flex min-h-[60px] flex-1 flex-col gap-2">
          {children}
          {prov.placeholder}
        </div>
      </div>
    )}
  </Droppable>
);

// ── Modal (informar horas / quebrar) ─────────────────────────────────────────────
const PendingModal: React.FC<{
  pending: Pending;
  employees: Option[];
  onClose: () => void;
  onInsert: (hours: number) => void;
  onPartial: (hours: number) => void;
  onSplit: (otherEmployeeId: number, hours: number) => void;
}> = ({ pending, employees, onClose, onInsert, onPartial, onSplit }) => {
  const [hours, setHours] = useState<string>(String(pending.defaultHours || ""));
  const [other, setOther] = useState<number | null>(null);

  const title =
    pending.kind === "insert" ? "Inserir horas na semana" : pending.kind === "partial" ? "Remover horas da semana" : "Quebrar para outro responsável";

  const confirm = () => {
    const h = Number(hours);
    if (!(h > 0)) return;
    if (pending.kind === "insert") onInsert(h);
    else if (pending.kind === "partial") onPartial(h);
    else {
      if (other == null) return;
      onSplit(other, h);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 nina-surface shadow-dv-lg dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-navy-700 dark:text-white">{title}</h3>
        <p className="mt-1 truncate text-xs text-gray-700 dark:text-gray-300">{pending.task.title}</p>

        {pending.kind === "split" && (
          <div className="mt-3">
            <label htmlFor="pending-modal-other" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">Novo responsável <span className="text-red-500">*</span></label>
            <SearchableSelect
              placeholder="Selecione…"
              value={other}
              onChange={(v) => setOther(v == null ? null : Number(v))}
              options={employees.map((e) => ({ label: e.name, value: e.id }))}
            />
          </div>
        )}

        <div className="mt-3">
          <label htmlFor="pending-modal-hours" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">Horas <span className="text-red-500">*</span></label>
          <input
            id="pending-modal-hours"
            type="number"
            min={0}
            step={0.5}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            autoFocus
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700">Cancelar</button>
          <button
            type="button"
            onClick={confirm}
            disabled={!(Number(hours) > 0) || (pending.kind === "split" && other == null)}
            className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CapacityEmployeePlanner;
